import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { Subject, takeUntil } from 'rxjs';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { ApiService } from '../../core/services/api.service';
import { PaginationParams } from '../../core/models/api.models';
import { PmRealtimeService } from '../../core/services/pm-realtime.service';
import { PmAdvancedApiService } from '../pm-advanced/pm-advanced.service';
import { SavedIssueFilterDto } from '../pm-advanced/pm-advanced.models';
import { KanbanApiService } from './kanban.service';
import { IssueDrawerService } from '../../shared/issue-drawer/issue-drawer.service';
import { deadlineBadgeClass, deadlineDisplayLabel } from '../shared/deadline.utils';
import {
  ISSUE_STATUS_OPTIONS,
  KanbanBoardDto,
  KanbanBoardViewDto,
  KanbanColumnViewDto,
  KanbanIssueCardDto,
  KanbanSwimlaneDto,
  SWIMLANE_TYPE_LABELS
} from './kanban.models';

interface ProjectRow {
  id: string;
  name: string;
}

interface ColumnView {
  id: string;
  name: string;
  mappedIssueStatus: number;
  mappedIssueStatusLabel?: string;
  sortOrder: number;
  wipLimit?: number;
  issueCount: number;
  isWipExceeded: boolean;
  swimlanes: SwimlaneView[];
}

interface SwimlaneView {
  key: string;
  label: string;
  issues: KanbanIssueCardDto[];
}

@Component({
  selector: 'app-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DragDropModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatCheckboxModule
  ],
  templateUrl: './kanban.component.html',
  styleUrl: './kanban.component.scss'
})
export class KanbanComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly kanbanApi = inject(KanbanApiService);
  private readonly pmAdvanced = inject(PmAdvancedApiService);
  private readonly realtime = inject(PmRealtimeService);
  private readonly issueDrawer = inject(IssueDrawerService);
  private readonly destroy$ = new Subject<void>();
  private joinedBoardId: string | null = null;

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly liveHint = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly boards = signal<KanbanBoardDto[]>([]);
  readonly board = signal<KanbanBoardViewDto | null>(null);
  readonly columns = signal<ColumnView[]>([]);
  readonly selectedProjectId = signal<string | null>(null);
  readonly selectedBoardId = signal<string | null>(null);
  readonly savedFilters = signal<SavedIssueFilterDto[]>([]);

  newBoardName = '';
  newColumnName = '';
  newColumnStatus = 0;
  newColumnWip: number | null = null;
  swimlaneType = 0;

  filterSearch = '';
  filterAssigneeId = '';
  filterEpicId = '';
  filterLabelId = '';
  filterType: string = '';
  filterPriority: string = '';
  filterUnassigned = false;
  savedFilterId = '';

  readonly selectionMode = signal(false);
  readonly selectedIssueIds = signal<Set<string>>(new Set());
  bulkAssigneeId = '';
  bulkTargetColumnId = '';

  readonly swimlaneOptions = Object.entries(SWIMLANE_TYPE_LABELS).map(([v, l]) => ({
    value: Number(v),
    label: l
  }));
  readonly statusOptions = ISSUE_STATUS_OPTIONS;

  ngOnInit(): void {
    this.loadProjects();
    this.realtime.issueMoved$.pipe(takeUntil(this.destroy$)).subscribe((evt) => {
      if (evt.boardId === this.selectedBoardId()) {
        this.liveHint.set(`Live update: ${evt.key} moved`);
        this.refreshBoard();
      }
    });
    this.realtime.issueUpdated$.pipe(takeUntil(this.destroy$)).subscribe((evt) => {
      if (evt.projectId === this.selectedProjectId()) {
        this.liveHint.set(`Live update: ${evt.key} ${evt.eventType}`);
        if (this.selectedBoardId()) this.refreshBoard();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.joinedBoardId) {
      void this.realtime.leaveBoard(this.joinedBoardId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.api.getPaged<ProjectRow>('v1/Projects', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.projects.set(page.items ?? []);
        if (page.items?.length && !this.selectedProjectId()) {
          this.selectProject(page.items[0].id);
        }
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  selectProject(projectId: string): void {
    this.selectedProjectId.set(projectId);
    this.selectedBoardId.set(null);
    this.board.set(null);
    this.columns.set([]);
    this.savedFilterId = '';
    this.loadSavedFilters(projectId);
    this.loadBoards(projectId);
  }

  loadSavedFilters(projectId: string): void {
    this.pmAdvanced.listFilters(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.savedFilters.set(Array.isArray(items) ? items : []),
      error: () => this.savedFilters.set([])
    });
  }

  loadBoards(projectId: string): void {
    this.loading.set(true);
    this.kanbanApi.listBoards(projectId).subscribe({
      next: (items) => {
        const list = Array.isArray(items) ? items : [];
        this.boards.set(list);
        this.loading.set(false);
        const preferred = list.find(b => b.isDefault) ?? list[0];
        if (preferred) {
          this.selectBoard(preferred.id);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load boards.');
      }
    });
  }

  selectBoard(boardId: string): void {
    if (this.joinedBoardId && this.joinedBoardId !== boardId) {
      void this.realtime.leaveBoard(this.joinedBoardId);
    }
    this.selectedBoardId.set(boardId);
    this.joinedBoardId = boardId;
    const boardMeta = this.boards().find(b => b.id === boardId);
    this.savedFilterId = boardMeta?.defaultSavedFilterId ?? '';
    void this.realtime.joinBoard(boardId).catch(() => {
      this.liveHint.set('Realtime unavailable — board will refresh on actions.');
    });
    this.refreshBoard();
  }

  refreshBoard(): void {
    const boardId = this.selectedBoardId();
    if (!boardId) return;
    this.loading.set(true);
    this.error.set(null);

    const params: PaginationParams = { page: 1, pageSize: 100 };
    if (this.filterSearch.trim()) params['search'] = this.filterSearch.trim();
    if (this.filterAssigneeId) params['assigneeId'] = this.filterAssigneeId;
    if (this.filterEpicId) params['epicId'] = this.filterEpicId;
    if (this.filterLabelId) params['labelId'] = this.filterLabelId;
    if (this.filterType !== '') params['type'] = this.filterType;
    if (this.filterPriority !== '') params['priority'] = this.filterPriority;
    if (this.filterUnassigned) params['unassignedOnly'] = true;
    if (this.savedFilterId) params['savedFilterId'] = this.savedFilterId;

    this.kanbanApi.getBoardView(boardId, params).subscribe({
      next: (view) => {
        this.board.set(view);
        this.swimlaneType = view.swimlaneType;
        if (view.defaultSavedFilterId && !this.savedFilterId) {
          this.savedFilterId = view.defaultSavedFilterId;
        }
        this.columns.set((view.columns ?? []).map(c => this.toColumnView(c)));
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load board.');
      }
    });
  }

  clearFilters(): void {
    this.filterSearch = '';
    this.filterAssigneeId = '';
    this.filterEpicId = '';
    this.filterLabelId = '';
    this.filterType = '';
    this.filterPriority = '';
    this.filterUnassigned = false;
    this.savedFilterId = '';
    this.clearSelection();
    this.refreshBoard();
  }

  saveBoardDefaultFilter(): void {
    const board = this.board();
    if (!board) return;
    this.kanbanApi.updateBoard(board.id, {
      name: board.name,
      description: board.description,
      isDefault: board.isDefault,
      swimlaneType: board.swimlaneType,
      defaultSavedFilterId: this.savedFilterId || null
    }).subscribe({
      next: (updated) => {
        this.boards.update(list => list.map(b => b.id === updated.id ? { ...b, ...updated } : b));
        this.liveHint.set('Board default filter saved.');
      },
      error: (err) => this.error.set(err?.message || 'Failed to save board default filter.')
    });
  }

  toggleSelectionMode(): void {
    this.selectionMode.update(v => !v);
    if (!this.selectionMode()) {
      this.clearSelection();
    }
  }

  isSelected(issueId: string): boolean {
    return this.selectedIssueIds().has(issueId);
  }

  toggleIssueSelection(issueId: string, event?: Event): void {
    event?.stopPropagation();
    this.selectedIssueIds.update(set => {
      const next = new Set(set);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  }

  clearSelection(): void {
    this.selectedIssueIds.set(new Set());
    this.bulkAssigneeId = '';
    this.bulkTargetColumnId = '';
  }

  selectedCount(): number {
    return this.selectedIssueIds().size;
  }

  bulkAssign(): void {
    const boardId = this.selectedBoardId();
    const ids = [...this.selectedIssueIds()];
    if (!boardId || !ids.length) return;
    this.kanbanApi.bulkAssign(boardId, ids, this.bulkAssigneeId || null).subscribe({
      next: () => {
        this.clearSelection();
        this.refreshBoard();
      },
      error: (err) => this.error.set(err?.message || 'Bulk assign failed.')
    });
  }

  bulkMove(): void {
    const boardId = this.selectedBoardId();
    const ids = [...this.selectedIssueIds()];
    const colId = this.bulkTargetColumnId;
    if (!boardId || !ids.length || !colId) return;
    const column = this.columns().find((c: ColumnView) => c.id === colId);
    const status = column?.mappedIssueStatus;
    if (status == null) {
      this.error.set('Target column has no mapped status.');
      return;
    }
    this.kanbanApi.bulkMove(boardId, ids, status).subscribe({
      next: () => {
        this.clearSelection();
        this.refreshBoard();
      },
      error: (err) => this.error.set(err?.message || 'Bulk move failed.')
    });
  }

  createBoard(): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.newBoardName.trim()) return;

    this.kanbanApi.createBoard({
      projectId,
      name: this.newBoardName.trim(),
      isDefault: this.boards().length === 0,
      swimlaneType: this.swimlaneType,
      createDefaultColumns: true
    }).subscribe({
      next: (board) => {
        this.newBoardName = '';
        this.loadBoards(projectId);
        if (board?.id) this.selectBoard(board.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to create board.')
    });
  }

  updateSwimlane(): void {
    const current = this.board();
    if (!current) return;
    this.kanbanApi.updateBoard(current.id, {
      name: current.name,
      description: current.description,
      isDefault: current.isDefault,
      swimlaneType: this.swimlaneType
    }).subscribe({
      next: () => this.refreshBoard(),
      error: (err) => this.error.set(err?.message || 'Failed to update swimlanes.')
    });
  }

  addColumn(): void {
    const boardId = this.selectedBoardId();
    if (!boardId || !this.newColumnName.trim()) return;
    this.kanbanApi.addColumn(boardId, {
      name: this.newColumnName.trim(),
      mappedIssueStatus: this.newColumnStatus,
      wipLimit: this.newColumnWip ?? undefined
    }).subscribe({
      next: () => {
        this.newColumnName = '';
        this.newColumnWip = null;
        this.refreshBoard();
      },
      error: (err) => this.error.set(err?.message || 'Failed to add column.')
    });
  }

  saveColumnWip(column: ColumnView): void {
    const boardId = this.selectedBoardId();
    if (!boardId) return;
    this.kanbanApi.updateColumn(boardId, column.id, {
      name: column.name,
      mappedIssueStatus: column.mappedIssueStatus,
      wipLimit: column.wipLimit ?? null,
      sortOrder: column.sortOrder
    }).subscribe({
      next: () => this.refreshBoard(),
      error: (err) => this.error.set(err?.message || 'Failed to update WIP limit.')
    });
  }

  drop(event: CdkDragDrop<KanbanIssueCardDto[]>, column: ColumnView): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    const issue = event.container.data[event.currentIndex];
    const boardId = this.selectedBoardId();
    if (!issue || !boardId) return;

    this.kanbanApi.moveIssue(boardId, issue.id, column.id, true).subscribe({
      next: (updated) => {
        issue.status = updated.status;
        issue.statusLabel = updated.statusLabel;
        this.refreshBoard();
      },
      error: (err) => {
        this.error.set(err?.message || 'Move blocked (check WIP limit).');
        this.refreshBoard();
      }
    });
  }

  connectedLists(): string[] {
    const ids: string[] = [];
    for (const col of this.columns()) {
      for (const lane of col.swimlanes) {
        ids.push(this.dropId(col.id, lane.key));
      }
    }
    return ids;
  }

  dropId(columnId: string, swimlaneKey: string): string {
    return `${columnId}__${swimlaneKey}`;
  }

  openIssueDrawer(issue: KanbanIssueCardDto, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.selectionMode()) return;
    this.issueDrawer.openById(issue.id);
  }

  priorityBadgeClass(priority: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--priority-lowest',
      1: 'tt-badge--priority-low',
      2: 'tt-badge--priority-medium',
      3: 'tt-badge--priority-high',
      4: 'tt-badge--priority-highest'
    };
    return map[priority] ?? 'tt-badge--priority-medium';
  }

  deadlineBadgeClass = deadlineBadgeClass;
  issueDeadlineLabel = deadlineDisplayLabel;

  private toColumnView(c: KanbanColumnViewDto): ColumnView {
    return {
      id: c.id,
      name: c.name,
      mappedIssueStatus: c.mappedIssueStatus,
      mappedIssueStatusLabel: c.mappedIssueStatusLabel,
      sortOrder: c.sortOrder,
      wipLimit: c.wipLimit,
      issueCount: c.issueCount,
      isWipExceeded: c.isWipExceeded,
      swimlanes: (c.swimlanes ?? []).map((s: KanbanSwimlaneDto) => ({
        key: s.key,
        label: s.label,
        issues: [...(s.issues ?? [])]
      }))
    };
  }
}
